import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { useToast } from '../lib/useToast.ts';
import { useDeckMutation } from '../lib/useDeckMutation.ts';
import { groupBlocks, baseOrder, resolveReorder, baseOf } from '../lib/slideBlocks.ts';
import { warmGallery } from '../lib/gallery.ts';
import { errorMessage } from '../lib/api.ts';
import type { DeckSummaryPayload, SlideFromUploadPayload } from '../../shared/api.ts';
import type { SectionRef, SlideEntry } from '../../shared/deck.ts';
import type { SlideBlock, SectionMove } from '../lib/slideBlocks.ts';

export type DeckEditorOptions = {
  slug: string;
  initialSlideId?: string | null;
  /** Sections created here carry no SectionDivider stub. True for a library
   *  item, which holds exactly its curated slides and no structural filler. */
  noDividers?: boolean;
  /** Fires ONLY on a deliberate focus-to-edit, never on ordinary navigation —
   *  see the note above about whole-deck chat scope. */
  onUserSelect?: (() => void) | null;
  /** The deck has no content sections left (its last one was deleted). */
  onEmptied?: (() => void) | null;
};

/** A section as the create dialog describes it. `insertAfter` is the key to
 *  land it behind (null = at the end); `noDivider` skips the structural
 *  divider stub, which bank items never want. */
export type NewSection = {
  key: string;
  name: string;
  color: string;
  insertAfter?: string | null;
  noDivider?: boolean;
};

/** Where the "Add a slide" chooser was opened from. There is deliberately no
 *  before/after: a new slide always lands directly AFTER `refSlide`, since the
 *  toggle that used to flip that was removed 2026-07-31. */
export type AddChooser = {
  /** null = the deck's tail (the filmstrip footer's "+ Slide"). */
  sectionKey?: string | null;
  refSlide?: SlideEntry | null;
} | null;

/**
 * A pick from the gallery iframe. Four shapes in one, told apart by which
 * key is present: a template (`pathTail`), a bank slide (`bank`), a slide
 * borrowed from another deck (`fromSlug`), or the text-list fallback
 * (`componentName`).
 *
 * Nullable as well as optional: `AddSlideChooser` normalises the postMessage
 * shape with `|| null` on every key it doesn't have, and every branch below
 * tests truthiness, so null and absent already mean the same thing here.
 */
export type TemplatePick = {
  title?: string | null;
  variant?: string | null;
  pathTail?: string | null;
  componentName?: string | null;
  fromSlug?: string | null;
  slideId?: string | null;
};

/** A whole library ITEM being copied in — one new section per source section. */
export type LibrarySectionPick = {
  /** The item's deck slug. */
  itemSlug: string;
  sectionName?: string;
  count?: number;
};

/** "Upload a finished slide": artwork designed outside the system, already
 *  re-encoded client-side by `lib/encodeSlideImage.ts`. */
export type UploadedSlide = {
  blob: Blob;
  filename: string;
  title?: string;
  /** False drops the RKH frame — full-bleed artwork brings its own. */
  chrome?: boolean;
  fit?: 'cover' | 'contain';
};

type DeckEditorState = {
  status: 'loading' | 'ready' | 'error';
  summary: DeckSummaryPayload | null;
  error: string | null;
};

/**
 * The editor core — ONE source of truth for everything the deck editor and
 * the library item editor share: the summary fetch/refresh pipeline, the
 * slide/section view, selection (incl. follow-the-slide after mutations),
 * and every direct manipulation handler (drag reorder, duplicate, delete,
 * move, section CRUD/reorder, the Add-a-slide chooser's deterministic
 * inserts). Canvas.tsx renders it over a whole deck; the library's
 * SlideEditorOverlay renders it over one item's deck.
 *
 * Both are now the same thing. Until 2026-08-21 a library item was one section
 * of a shared shelf deck, and this hook carried an `itemKey` filter, a shadow
 * `gIdx` index and a merge-back-into-the-full-list step to make that section
 * look self-contained. An item owns its deck now, so the view IS the deck and
 * all of that is gone.
 *
 * Surface-specific behaviour stays in the callers: chat scope and prefill,
 * drawers, save-to-library (Canvas); pinned scope, close-on-empty, the
 * clamped preview (overlay). `onUserSelect` fires ONLY on a deliberate
 * focus-to-edit (an "Empty — add your own" insert that wants the new slide's
 * Edit tab) so Canvas can re-engage slide scope without the hook knowing chat
 * exists. Ordinary navigation (tile click, arrow keys, preview nav) does NOT
 * fire it: whole-deck scope stays sticky until the user clicks the toggle.
 */
export function useDeckEditor({
  slug,
  noDividers = false,
  initialSlideId = null,
  onUserSelect = null,
  onEmptied = null,
}: DeckEditorOptions) {
  const [state, setState] = useState<DeckEditorState>({ status: 'loading', summary: null, error: null });
  // View-space index of the active slide (view == deck when unfiltered).
  const [activeIdx, setActiveIdx] = useState(0);
  // Open "Add a slide" chooser: { sectionKey, refSlide, position } | null.
  const [addChooser, setAddChooser] = useState<AddChooser>(null);

  // Bumped to refetch the summary (deck mutations).
  const [reloadToken, setReloadToken] = useState(0);
  // Bumped after any apply / mutation so PreviewFrame force-reloads the
  // iframe (HMR doesn't propagate through the studio's HTTP-only proxy).
  const [previewReloadKey, setPreviewReloadKey] = useState(0);
  const bumpPreviewReload = () => setPreviewReloadKey((k) => k + 1);
  // After a deck mutation, select this slide id once the refreshed summary
  // surfaces it (create_slide jumps to the new slide; reorder/move/delete
  // keep or fix the selection by id, since indexes shift).
  const [pendingNewSlideId, setPendingNewSlideId] = useState<string | null>(initialSlideId);
  // One-shot: an "Empty — add your own" gallery insert lands the user on the
  // Edit tab (the Add… button is the whole point of those variants).
  // CommandBar consumes it once the new slide is focused.
  const [forceEditTab, setForceEditTab] = useState(false);
  // Optimistic FULL slide list during an in-flight reorder — cleared the
  // moment a fresh summary lands (or the mutation reverts).
  const [slidesOverride, setSlidesOverride] = useState<SlideEntry[] | null>(null);

  // Spawn the gallery's Vite child now, not on first + click.
  useEffect(() => {
    warmGallery();
  }, []);

  const { toast, pushToast, dismissToast, runUndo } = useToast();
  const mutate = useDeckMutation({ refreshSummary, bumpPreviewReload, pushToast });

  useEffect(() => {
    let cancelled = false;
    setState((s) => (s.summary ? s : { status: 'loading', summary: null, error: null }));
    api
      .get<DeckSummaryPayload>(`/api/slides/${slug}/_summary`)
      .then((data) => {
        if (cancelled) return;
        setState({ status: 'ready', summary: data, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ status: 'error', summary: null, error: errorMessage(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, reloadToken]);

  const summary = state.summary;
  const sections = summary?.sections || [];
  // Memoised on its two inputs rather than rebuilt from `||` each render: the
  // position-restore effect below depends on it, and a fresh array identity
  // every render would re-run it forever.
  const slides = useMemo(() => slidesOverride || summary?.slides || [], [slidesOverride, summary?.slides]);

  // A fresh summary replaces any optimistic list and re-clamps the selection;
  // a deck whose last content section went reports up instead.
  useEffect(() => {
    if (!state.summary) return;
    setSlidesOverride(null);
    if (onEmptied && state.status === 'ready' && sections.length === 0) {
      onEmptied();
      return;
    }
    const total = slides.length;
    if (total > 0) setActiveIdx((i) => Math.min(i, total - 1));
  }, [state.summary]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeSlide = slides[activeIdx] || null;

  // Refresh survival: a full page reload used to dump the editor back on
  // slide 1 (Jake, 2026-07-29). The active slide is kept in sessionStorage
  // (per deck) and restored once, when the first slide list lands —
  // sessionStorage so a NEW tab still starts at slide 1, only a refresh of
  // this tab restores. An explicit initialSlideId (library overlay deep-link)
  // wins over the stored position.
  const posKey = `studio.lastSlide.${slug}`;
  const [posRestored, setPosRestored] = useState(false);
  useEffect(() => {
    if (posRestored || state.status !== 'ready' || slides.length === 0) return;
    setPosRestored(true);
    if (initialSlideId) return;
    let stored = NaN;
    try {
      stored = parseInt(sessionStorage.getItem(posKey) || '', 10);
    } catch {
      /* storage unavailable */
    }
    if (Number.isInteger(stored) && stored > 0 && stored < slides.length) setActiveIdx(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, slides.length]);
  useEffect(() => {
    if (!posRestored) return;
    try {
      sessionStorage.setItem(posKey, String(activeIdx));
    } catch {
      /* storage unavailable */
    }
  }, [activeIdx, posRestored, posKey]);

  useEffect(() => {
    if (!pendingNewSlideId || state.status !== 'ready') return;
    const idx = slides.findIndex((s) => s.id === pendingNewSlideId);
    if (typeof idx === 'number' && idx >= 0) {
      // Move the preview, but keep the chat scope the user chose: applying a
      // create_slide card from deck chat must not yank them into slide scope
      // while sibling Apply cards still live in the deck thread.
      setActiveIdx(idx);
      setPendingNewSlideId(null);
    }
  }, [pendingNewSlideId, state.status, slides]);

  // ←/→ slide nav, Alt+↑/↓ moves the focused slide (skipped while typing).
  useEffect(() => {
    if (state.status !== 'ready') return;
    const total = slides.length;
    if (!total) return;
    function isEditingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      const tag = t.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable;
    }
    function onKey(e: KeyboardEvent) {
      if (isEditingTarget(e.target)) return;
      if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        if (activeSlide) moveSlide(activeSlide, e.key === 'ArrowUp' ? -1 : 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        selectSlide(Math.max(0, activeIdx - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        selectSlide(Math.min(total - 1, activeIdx + 1));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.status, slides, activeIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  function selectSlide(idx: number) {
    // Move the preview/filmstrip only — deliberately NOT onUserSelect, which
    // means "focus this slide to edit it". Navigating does still release
    // whole-deck scope, but on Canvas's terms, not here: it hands back only
    // once the deck work is settled (lib/chatScope.js), so a scroll during a
    // running deck turn or an open review can't re-aim the chat mid-flight.
    // The one deliberate re-engage is the empty-variant insert in
    // handleAddFromTemplate, which calls onUserSelect itself.
    setActiveIdx(idx);
  }

  function jumpToSlide(slideId: string) {
    // Exact id first; deck-scope proposal cards carry base (file-level) ids,
    // so fall back to the first instance of a multi: N block.
    let idx = slides.findIndex((s) => s.id === slideId);
    if (idx < 0) idx = slides.findIndex((s) => baseOf(s.id) === slideId);
    if (typeof idx === 'number' && idx >= 0) selectSlide(idx);
  }

  function refreshSummary() {
    setReloadToken((t) => t + 1);
    bumpPreviewReload();
  }

  const sectionName = (key: string) => sections.find((s) => s.key === key)?.name || key;

  /** Preview navigation reports deck indexes, which are the view's indexes. */
  function handlePreviewActiveChange(idx: number) {
    if (idx >= 0 && idx < slides.length) selectSlide(idx);
    return undefined;
  }

  // ----- direct mutations (filmstrip) --------------------------------------
  // Every handler funnels through mutate.run → the same refreshSummary /
  // bumpPreviewReload pipeline the chat Apply path uses, plus a toast (with
  // Undo where the inverse is a cheap call).

  /** Shared by drag-drop and Alt+arrow: persist a new block order, moving
   *  the slide to `toSection` first when the drop crossed a boundary. */
  // `moved` is the block's FIRST SLIDE, not the block: both call sites pass
  // `move.moved.items[0]`, and this reads its id and title.
  async function commitReorder(newList: SlideEntry[], moved: SlideEntry, toSection: string | null) {
    const prevList = slides;
    const prevOrder = baseOrder(slides);
    const newOrder = baseOrder(newList);
    const fromSection = moved.section;
    const keepId = activeSlide?.id;
    const moveUrl = `/api/slides/${slug}/slide/section?id=${encodeURIComponent(moved.id)}`;
    await mutate.run(
      async () => {
        if (toSection) await api.patch(moveUrl, { section: toSection });
        await api.post(`/api/manifest/${slug}/reorder`, { order: newOrder });
      },
      {
        optimistic: () => {
          setSlidesOverride(newList);
          if (keepId) {
            const i = newList.findIndex((s) => s.id === keepId);
            if (i >= 0) setActiveIdx(i);
          }
        },
        revert: () => {
          setSlidesOverride(null);
          if (keepId) {
            const i = prevList.findIndex((s) => s.id === keepId);
            if (i >= 0) setActiveIdx(i);
          }
        },
        toast: toSection ? `Moved "${moved.title}" to ${sectionName(toSection)}` : 'Slides reordered',
        undo: async () => {
          if (toSection) await api.patch(moveUrl, { section: fromSection });
          await api.post(`/api/manifest/${slug}/reorder`, { order: prevOrder });
          refreshSummary();
        },
      },
    );
    if (keepId) setPendingNewSlideId(keepId);
  }

  /** Multi-drag commit: same shape as commitReorder but for a whole selection.
   *  `sectionMoves` ([{slide, from}], from resolveMultiReorder) lists every
   *  slide whose section changed — retagged in ONE batch call, and undone
   *  per ORIGINAL section (a selection can span several). */
  async function commitMultiReorder(
    newList: SlideEntry[],
    movedBlocks: SlideBlock[],
    toSection: string | null,
    sectionMoves: SectionMove[],
  ) {
    const prevList = slides;
    const prevOrder = baseOrder(slides);
    const newOrder = baseOrder(newList);
    const keepId = activeSlide?.id;
    const movedCount = movedBlocks.reduce((n, b) => n + b.items.length, 0);
    const batchUrl = `/api/slides/${slug}/slides/section`;
    await mutate.run(
      async () => {
        if (sectionMoves.length) {
          await api.patch(batchUrl, { ids: sectionMoves.map((m) => m.slide.id), section: toSection });
        }
        await api.post(`/api/manifest/${slug}/reorder`, { order: newOrder });
      },
      {
        optimistic: () => {
          setSlidesOverride(newList);
          if (keepId) {
            const i = newList.findIndex((s) => s.id === keepId);
            if (i >= 0) setActiveIdx(i);
          }
        },
        revert: () => {
          setSlidesOverride(null);
          if (keepId) {
            const i = prevList.findIndex((s) => s.id === keepId);
            if (i >= 0) setActiveIdx(i);
          }
        },
        toast: toSection
          ? `Moved ${movedCount} slide${movedCount === 1 ? '' : 's'} to ${sectionName(toSection)}`
          : `${movedCount} slide${movedCount === 1 ? '' : 's'} reordered`,
        undo: async () => {
          const byOrigin = new Map();
          for (const m of sectionMoves) {
            if (!byOrigin.has(m.from)) byOrigin.set(m.from, []);
            byOrigin.get(m.from).push(m.slide.id);
          }
          for (const [from, ids] of byOrigin) await api.patch(batchUrl, { ids, section: from });
          await api.post(`/api/manifest/${slug}/reorder`, { order: prevOrder });
          refreshSummary();
        },
      },
    );
    if (keepId) setPendingNewSlideId(keepId);
  }

  /** Move a slide's block one position — Alt+↑/↓ on the focused slide, and the
   *  ⋮ menu's Move up / Move down. Adopts the section of the block it swaps
   *  past when that crosses a boundary, exactly as a drag to the same slot
   *  would. Both share `resolveReorder` with the drag surfaces. */
  function moveSlide(slide: SlideEntry, dir: -1 | 1) {
    if (!slide || mutate.busy) return;
    const blocks = groupBlocks(slides);
    const bIdx = blocks.findIndex((b) => b.baseId === baseOf(slide.id));
    const target = bIdx + dir;
    if (bIdx < 0 || target < 0 || target >= blocks.length) return;
    const move = resolveReorder(blocks, bIdx, target);
    const first = move?.moved.items[0];
    if (move && first) commitReorder(move.newList, first, move.toSection);
  }

  async function handleDuplicate(slide: SlideEntry) {
    const res = await mutate.run(
      () => api.post<{ newSlideId?: string }>(`/api/slides/${slug}/slide/duplicate?id=${encodeURIComponent(slide.id)}`),
      {
        toast: `Duplicated "${slide.title}"`,
        undo: async (r) => {
          if (!r?.newSlideId) return;
          await api.delete(`/api/slides/${slug}/slide?id=${encodeURIComponent(r.newSlideId)}`);
          refreshSummary();
        },
      },
    );
    if (res?.newSlideId) setPendingNewSlideId(res.newSlideId);
  }

  // Park a slide: it stays in the deck and stays editable, but the published
  // deck, the PDF and the library skip it. Reversible from the same menu item,
  // and the undo path is simply the opposite flag.
  async function handleToggleHidden(slide: SlideEntry) {
    const next = !slide.hidden;
    await mutate.run(
      () =>
        api.patch(`/api/slides/${slug}/slide/hidden?id=${encodeURIComponent(slide.id)}`, {
          hidden: next,
        }),
      {
        toast: next ? `Hid "${slide.title}": left out when published` : `"${slide.title}" is published again`,
        undo: async () => {
          await api.patch(`/api/slides/${slug}/slide/hidden?id=${encodeURIComponent(slide.id)}`, { hidden: !next });
          refreshSummary();
        },
      },
    );
    bumpPreviewReload();
  }

  // Accept this slide's layout (Edit tab → Slide → Layout check, and the
  // "Looks right" button on a geometry warning). Like `hidden` this is a
  // MANIFEST-row flag, so it has to go through the summary refresh: the Edit
  // panel reads the switch's position off `slide.layoutOk` in the slide list,
  // and only bumping the preview left that list stale — the switch sprang back
  // on the moment the user navigated away and returned.
  async function handleToggleLayoutOk(slide: SlideEntry, next: boolean): Promise<unknown> {
    const res = await mutate.run(
      () =>
        api.patch(`/api/slides/${slug}/slide/layout-ok?id=${encodeURIComponent(slide.id)}`, {
          layoutOk: next,
        }),
      {
        refresh: 'summary',
        toast: next ? `"${slide.title}" accepted as it is: layout warnings off for this slide` : null,
        undo: next ? () => handleToggleLayoutOk(slide, false) : null,
      },
    );
    bumpPreviewReload();
    return res;
  }

  // Pin this slide's WORDS (Edit tab → Slide → Keep my exact words). Manifest
  // row flag like `layoutOk`, so it goes through the summary refresh: the Edit
  // panel reads the switch's position off `slide.copyLocked` in the slide list,
  // and a write that only bumped the preview would spring back the moment the
  // user navigated away and returned.
  async function handleToggleCopyLocked(slide: SlideEntry, next: boolean): Promise<unknown> {
    const res = await mutate.run(
      () =>
        api.patch(`/api/slides/${slug}/slide/copy-locked?id=${encodeURIComponent(slide.id)}`, {
          copyLocked: next,
        }),
      {
        refresh: 'summary',
        toast: next
          ? `"${slide.title}" is locked to your exact words: Studio will resize or split it rather than reword it`
          : null,
        undo: next ? () => handleToggleCopyLocked(slide, false) : null,
      },
    );
    bumpPreviewReload();
    return res;
  }

  // Drop the top-right section label on ONE slide (manifest row flag). Only
  // ever offered when the deck shows the label at all — see EditPanel.
  async function handleToggleSectionChrome(slide: SlideEntry, next: boolean): Promise<unknown> {
    const res = await mutate.run(
      () =>
        api.patch(`/api/slides/${slug}/slide/section-chrome?id=${encodeURIComponent(slide.id)}`, {
          sectionChrome: next,
        }),
      {
        refresh: 'summary',
        toast: next ? null : `Section label hidden on "${slide.title}"`,
        undo: next ? null : () => handleToggleSectionChrome(slide, true),
      },
    );
    bumpPreviewReload();
    return res;
  }

  async function handleDeleteSlide(slide: SlideEntry) {
    const base = baseOf(slide.id);
    const prevList = slides;
    const res = await mutate.run(() => api.delete(`/api/slides/${slug}/slide?id=${encodeURIComponent(slide.id)}`), {
      toast: `Deleted "${slide.title}"`,
    });
    if (res === null) return;
    // Keep the selection sensible: stay on the active slide if it survives,
    // otherwise land on the deleted slide's previous neighbour.
    const activeBase = activeSlide ? baseOf(activeSlide.id) : null;
    if (activeSlide && activeBase && activeBase !== base) {
      setPendingNewSlideId(activeSlide.id);
    } else {
      const remaining = prevList.filter((s) => baseOf(s.id) !== base);
      const idx = prevList.findIndex((s) => baseOf(s.id) === base);
      const next = remaining[Math.min(Math.max(idx - 1, 0), remaining.length - 1)];
      if (next) setPendingNewSlideId(next.id);
    }
  }

  /** Delete several slides in one action (the overview's checkbox select).
   *  Deduped to one row per file; a single toast, no undo (delete removes the
   *  file, matching the single-slide delete). */
  async function handleDeleteSlides(toDelete: SlideEntry[]) {
    const seen = new Set<string>();
    const rows: SlideEntry[] = [];
    for (const s of toDelete || []) {
      const base = baseOf(s.id);
      if (!seen.has(base)) {
        seen.add(base);
        rows.push(s);
      }
    }
    if (rows.length === 0) return;
    const prevList = slides;
    const activeBase = activeSlide ? baseOf(activeSlide.id) : null;
    const res = await mutate.run(
      async () => {
        for (const s of rows) {
          await api.delete(`/api/slides/${slug}/slide?id=${encodeURIComponent(s.id)}`);
        }
        return { count: rows.length };
      },
      { toast: `Deleted ${rows.length} slide${rows.length === 1 ? '' : 's'}` },
    );
    if (res === null) return;
    // Keep the selection sensible: stay put if the active slide survived,
    // otherwise land on whatever remains at the top.
    if (activeSlide && activeBase && !seen.has(activeBase)) {
      setPendingNewSlideId(activeSlide.id);
    } else {
      const next = prevList.find((s) => !seen.has(baseOf(s.id)));
      if (next) setPendingNewSlideId(next.id);
    }
  }

  async function handleMoveToSection(slide: SlideEntry, sectionKey: string) {
    const from = slide.section;
    const url = `/api/slides/${slug}/slide/section?id=${encodeURIComponent(slide.id)}`;
    await mutate.run(() => api.patch(url, { section: sectionKey }), {
      toast: `Moved "${slide.title}" to ${sectionName(sectionKey)}`,
      undo: async () => {
        await api.patch(url, { section: from });
        refreshSummary();
      },
    });
    setPendingNewSlideId(slide.id);
  }

  function handleRenameSlide(slide: SlideEntry, title: string) {
    const url = `/api/slides/${slug}/slide/title?id=${encodeURIComponent(slide.id)}`;
    mutate.run(() => api.patch<{ prevTitle?: string }>(url, { title }), {
      toast: `Renamed slide to "${title}"`,
      undo: async (res) => {
        if (res?.prevTitle) await api.patch(url, { title: res.prevTitle });
        refreshSummary();
      },
    });
  }

  function handleRenameSection(section: SectionRef, name: string) {
    const prev = section.name || section.key;
    mutate.run(() => api.put(`/api/manifest/${slug}/sections/${section.key}`, { name }), {
      toast: `Renamed section to "${name}"`,
      undo: async () => {
        await api.put(`/api/manifest/${slug}/sections/${section.key}`, { name: prev });
        refreshSummary();
      },
    });
  }

  function handleRecolourSection(section: SectionRef, color: string) {
    const prev = section.color;
    mutate.run(() => api.put<{ cascaded?: number }>(`/api/manifest/${slug}/sections/${section.key}`, { color }), {
      toast: (res) => {
        const n = res?.cascaded || 0;
        return (
          `Recoloured "${section.name || section.key}" to ${color.toLowerCase()}` +
          (n ? ` · ${n} slide${n === 1 ? '' : 's'}` : '')
        );
      },
      undo: prev
        ? async () => {
            await api.put(`/api/manifest/${slug}/sections/${section.key}`, { color: prev });
            refreshSummary();
          }
        : null,
    });
  }

  /** Resolves with the created section's key, or null. */
  async function handleCreateSection({ key, name, color, insertAfter, noDivider }: NewSection) {
    const finalKey = key;
    const res = await mutate.run(
      () =>
        api.post(`/api/manifest/${slug}/sections`, {
          key: finalKey,
          name,
          color,
          insertAfter,
          ...(noDividers || noDivider ? { noDivider: true } : {}),
        }),
      {
        toast: `Added section "${name}"`,
        undo: async () => {
          await api.delete(`/api/manifest/${slug}/sections/${finalKey}`);
          refreshSummary();
        },
      },
    );
    return res === null ? null : finalKey;
  }

  /** `moveSlidesTo` keeps the section's slides by merging them into that
   *  section (deck order preserved); without it the slides die with the
   *  section. */
  function handleDeleteSection(section: SectionRef, count: number, moveSlidesTo: string | null = null) {
    const url =
      `/api/manifest/${slug}/sections/${section.key}` +
      (moveSlidesTo ? `?moveSlidesTo=${encodeURIComponent(moveSlidesTo)}` : '');
    mutate.run(() => api.delete(url), {
      toast: moveSlidesTo
        ? `Deleted section "${section.name || section.key}": ${count} slide${count === 1 ? '' : 's'} moved to ${sectionName(moveSlidesTo)}`
        : `Deleted section "${section.name || section.key}" (${count} slide${count === 1 ? '' : 's'})`,
    });
  }

  /** Move a content section one step up/down — within the item when
   *  filtered. Persists the new FULL section order (sections export +
   *  manifest blocks, renumbered server-side) and mirrors it optimistically
   *  by regrouping the slide list. */
  async function handleMoveSection(section: SectionRef, dir: -1 | 1) {
    const viewKeys = sections.map((s) => s.key).filter((k) => k !== 'intro' && k !== 'closing');
    const vi = viewKeys.indexOf(section.key);
    const vj = vi + dir;
    if (vi < 0 || vj < 0 || vj >= viewKeys.length) return;
    const next = viewKeys.slice();
    const a = next[vi];
    const b = next[vj];
    if (a === undefined || b === undefined) return;
    next[vi] = b;
    next[vj] = a;
    return applySectionOrder(next, `Moved "${section.name || section.key}" ${dir < 0 ? 'up' : 'down'}`);
  }

  /**
   * Drag-drop variant: the filmstrip hands back the view's content sections in
   * their NEW order (a collapsed section row dragged to a new slot). Same
   * persistence, optimism and undo as the ⋮ menu's one-step move.
   *
   * This is deliberately NOT the slide multi-reorder path: that one retags
   * every moved slide into whichever section it lands beside, which is exactly
   * the wrong answer when the thing being moved IS a section.
   */
  async function handleReorderSections(newViewContentKeys: string[], label: string) {
    const viewKeys = sections.map((s) => s.key).filter((k) => k !== 'intro' && k !== 'closing');
    if (
      newViewContentKeys.length !== viewKeys.length ||
      !newViewContentKeys.every((k) => viewKeys.includes(k)) ||
      newViewContentKeys.every((k, i) => k === viewKeys[i])
    ) {
      return; // not a permutation of the view's sections, or nothing moved
    }
    return applySectionOrder(newViewContentKeys, label || 'Moved section');
  }

  /** Persist a new order for the deck's content sections. */
  async function applySectionOrder(newViewContentKeys: string[], toastText: string) {
    const keys = sections.map((s) => s.key);
    const content = keys.filter((k) => k !== 'intro' && k !== 'closing');
    if (content.length !== newViewContentKeys.length) return;
    content.splice(0, content.length, ...newViewContentKeys);
    // Built by spreading rather than concat: filtering on a literal narrows
    // the element type to `'intro'[]`, and concat then refuses the plain
    // string[] of content keys.
    const order: string[] = [...keys.filter((k) => k === 'intro'), ...content, ...keys.filter((k) => k === 'closing')];
    const prevList = slides;
    const keepId = activeSlide?.id;
    const buckets = new Map<string, SlideEntry[]>(order.map((k): [string, SlideEntry[]] => [k, []]));
    // Slides in sections the summary doesn't know — keep at the end.
    const strays: SlideEntry[] = [];
    for (const s of slides) (buckets.get(s.section) || strays).push(s);
    const newFull = order
      .flatMap((k) => buckets.get(k) ?? [])
      .concat(strays)
      .map((s, idx) => ({ ...s, idx }));
    await mutate.run(() => api.post(`/api/manifest/${slug}/section-order`, { order }), {
      optimistic: () => {
        setSlidesOverride(newFull);
        if (keepId) {
          const k = newFull.findIndex((s) => s.id === keepId);
          if (k >= 0) setActiveIdx(k);
        }
      },
      revert: () => {
        setSlidesOverride(null);
        if (keepId) {
          const k = prevList.findIndex((s) => s.id === keepId);
          if (k >= 0) setActiveIdx(k);
        }
      },
      toast: toastText,
      undo: async () => {
        await api.post(`/api/manifest/${slug}/section-order`, { order: keys });
        refreshSummary();
      },
    });
    if (keepId) setPendingNewSlideId(keepId);
  }

  // ----- "Add a slide" (deterministic inserts; chat path stays in Canvas) --

  // The + buttons open the chooser. A new slide always lands directly AFTER
  // refSlide — every + in the studio means "one more here", and the Before/After
  // toggle that used to let you flip that was removed 2026-07-31: nothing ever
  // opened the chooser asking for "before", and reordering is a drag away.
  function handleAddSlide(sectionKey: string | null, refSlide: SlideEntry | null = null) {
    setForceEditTab(false); // a stale force never outlives the next chooser
    setAddChooser({ sectionKey, refSlide });
  }

  // `pick` comes from the gallery iframe — a template ({ pathTail, title,
  // variant }), a bank slide ({ bank, slideId, title }) from the library
  // shelf, a slide borrowed from another deck ({ fromSlug, slideId, title })
  // — or the text-list fallback ({ componentName, title }). Templates insert
  // via from-template; bank and other-deck slides are SNAPSHOT copies via
  // from-library / from-deck.
  async function handleAddFromTemplate(pick: TemplatePick) {
    const { sectionKey, refSlide } = addChooser || {};
    const section = sectionKey;
    const placement = {
      ...(section ? { section } : {}),
      ...(refSlide ? { insertAfterSlideId: refSlide.id } : {}),
    };
    const res = await mutate.run(
      (): Promise<{ slideId?: string }> =>
        pick.fromSlug
          ? api.post(`/api/slides/${slug}/slide/from-deck`, {
              fromSlug: pick.fromSlug,
              slideId: pick.slideId,
              ...placement,
            })
          : api.post(`/api/slides/${slug}/slide/from-template`, {
              template: pick.pathTail || pick.componentName,
              ...(pick.variant ? { variant: pick.variant } : {}),
              ...placement,
            }),
      {
        toast: `Added "${pick.title}"${pick.variant ? ` (${pick.variant})` : ''}`,
        undo: async (r) => {
          if (!r?.slideId) return;
          await api.delete(`/api/slides/${slug}/slide?id=${encodeURIComponent(r.slideId)}`);
          refreshSummary();
        },
      },
    );
    setAddChooser(null);
    if (res?.slideId) {
      setPendingNewSlideId(res.slideId);
      if (/^empty/i.test(pick.variant || '')) {
        setForceEditTab(true);
        onUserSelect?.(); // re-engage slide scope so the Edit tab can show
      }
    }
  }

  // "Add N selected" from the gallery: insert several picks in one action.
  // Each pick re-anchors to the previously inserted slide so they land in the
  // chosen order at the same spot, under one combined toast + undo.
  async function handleAddFromTemplateBatch(picks: TemplatePick[]) {
    if (!Array.isArray(picks) || picks.length === 0) return;
    const { sectionKey, refSlide } = addChooser || {};
    const section = sectionKey;
    let anchorId = refSlide?.id || null;
    const res = await mutate.run(
      async () => {
        const slideIds: string[] = [];
        for (const pick of picks) {
          const placement = {
            ...(section ? { section } : {}),
            ...(anchorId ? { insertAfterSlideId: anchorId } : {}),
          };
          const r: { slideId?: string } = pick.fromSlug
            ? await api.post(`/api/slides/${slug}/slide/from-deck`, {
                fromSlug: pick.fromSlug,
                slideId: pick.slideId,
                ...placement,
              })
            : await api.post(`/api/slides/${slug}/slide/from-template`, {
                template: pick.pathTail || pick.componentName,
                ...(pick.variant ? { variant: pick.variant } : {}),
                ...placement,
              });
          if (r?.slideId) {
            slideIds.push(r.slideId);
            // Chain the rest after this one so the order is preserved.
            anchorId = r.slideId;
          }
        }
        return { slideIds };
      },
      {
        toast: `Added ${picks.length} slide${picks.length === 1 ? '' : 's'}`,
        undo: async (r) => {
          for (const id of (r.slideIds || []).slice().reverse()) {
            await api.delete(`/api/slides/${slug}/slide?id=${encodeURIComponent(id)}`).catch(() => {});
          }
          refreshSummary();
        },
      },
    );
    setAddChooser(null);
    const lastAdded = res?.slideIds?.[res.slideIds.length - 1];
    if (lastAdded) setPendingNewSlideId(lastAdded);
  }

  // "Upload a finished slide" (expert path): artwork designed outside the
  // system, already re-encoded client-side (lib/encodeSlideImage.js), posted
  // as multipart to from-upload — asset + slide land in one transaction.
  async function handleUploadSlide({ blob, filename, title, chrome, fit }: UploadedSlide) {
    const { sectionKey, refSlide } = addChooser || {};
    const section = sectionKey;
    const form = new FormData();
    form.append('file', blob, filename);
    if (title) form.append('title', title);
    if (!chrome) form.append('chrome', '0');
    if (fit === 'contain') form.append('fit', 'contain');
    if (section) form.append('section', section);
    if (refSlide) form.append('insertAfterSlideId', refSlide.id);
    const res = await mutate.run(
      () => api.postForm<SlideFromUploadPayload>(`/api/slides/${slug}/slide/from-upload`, form),
      {
        toast: `Added "${title || filename}"`,
        undo: async (r) => {
          if (!r?.slideId) return;
          await api.delete(`/api/slides/${slug}/slide?id=${encodeURIComponent(r.slideId)}`);
          if (r.assetName) {
            await api.delete(`/api/assets/${slug}?name=${encodeURIComponent(r.assetName)}`).catch(() => {});
          }
          refreshSummary();
        },
      },
    );
    setAddChooser(null);
    if (res?.slideId) setPendingNewSlideId(res.slideId);
  }

  // "Add all N slides" on a library item: the server copies the whole item
  // into this deck — one new section per source section, names/colours as
  // authored, no dividers — all-or-nothing with rollback server-side.
  async function handleAddSectionFromLibrary(pick: LibrarySectionPick) {
    const { refSlide, sectionKey } = addChooser || {};
    const insertAfterSection = refSlide?.section || sectionKey || undefined;
    const res = await mutate.run(
      () =>
        api.post<{ sectionKeys?: string[]; sectionKey?: string; slideIds?: string[] }>(
          `/api/slides/${slug}/section/from-library`,
          {
            itemSlug: pick.itemSlug,
            ...(insertAfterSection ? { insertAfterSection } : {}),
          },
        ),
      {
        toast: `Added "${pick.sectionName}" (${pick.count} slides)`,
        undo: async (r) => {
          for (const k of (r?.sectionKeys || [r?.sectionKey]).slice().reverse()) {
            if (!k) continue;
            await api.delete(`/api/manifest/${slug}/sections/${k}`);
          }
          refreshSummary();
        },
      },
    );
    setAddChooser(null);
    if (res?.slideIds?.[0]) setPendingNewSlideId(res.slideIds[0]);
  }

  return {
    // data
    status: state.status,
    error: state.error,
    summary,
    sections,
    slides,
    activeIdx,
    activeSlide,
    // Deck-order index for PreviewFrame (null until slides exist — callers
    // fall back to their own initial idx).
    // selection + refresh
    selectSlide,
    jumpToSlide,
    setPendingNewSlideId,
    refreshSummary,
    bumpPreviewReload,
    previewReloadKey,
    handlePreviewActiveChange,
    // plumbing
    mutate,
    toast: { toast, pushToast, dismissToast, runUndo },
    sectionName,
    // one-shot Edit-tab force after Empty-variant inserts
    forceEditTab,
    // Clicking a geometry warning badge in the preview opens the Edit tab on
    // the finding — the badge is the only thing on screen when a slide is
    // flagged, so it has to lead somewhere.
    openEditTab: () => setForceEditTab(true),
    clearForceEditTab: () => setForceEditTab(false),
    // add-a-slide chooser
    addChooser,
    setAddChooser,
    handleAddSlide,
    handleAddFromTemplate,
    handleAddFromTemplateBatch,
    handleUploadSlide,
    handleAddSectionFromLibrary,
    // direct mutations
    commitReorder,
    commitMultiReorder,
    moveSlide,
    handleDuplicate,
    handleToggleHidden,
    handleToggleLayoutOk,
    handleToggleCopyLocked,
    handleToggleSectionChrome,
    handleDeleteSlide,
    handleDeleteSlides,
    handleMoveToSection,
    handleRenameSection,
    handleRenameSlide,
    handleRecolourSection,
    handleCreateSection,
    handleDeleteSection,
    handleMoveSection,
    handleReorderSections,
  };
}
